import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Calendar, 
  List, 
  Ban, 
  ArchiveRestore, 
  Trash2, 
  ChevronLeft, 
  ChevronRight,
  X,
  XCircle,
  AlertTriangle,
  CheckCircle2,
  Users
} from "lucide-react";
import { useState, useMemo, CSSProperties, useEffect } from "react";
import { Link } from "wouter";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/useMobile";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { hasAnyRole } from "@/lib/roles";
import { CancelSessionDialog } from "@/components/CancelSessionDialog";
import { ReactivateSessionDialog } from "@/components/ReactivateSessionDialog";

// Day names in German
const DAY_NAMES = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

// Format date for display
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T12:00:00Z");
  return date.toLocaleDateString("de-DE", { 
    weekday: "short", 
    day: "2-digit", 
    month: "2-digit", 
    year: "numeric" 
  });
}

// Format date for input
function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Get month name in German
function getMonthName(date: Date): string {
  return date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
}

// Get days in month
function getDaysInMonth(year: number, month: number): Date[] {
  const days: Date[] = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  
  // Add padding days from previous month
  const startPadding = firstDay.getDay();
  for (let i = startPadding - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push(d);
  }
  
  // Add days of current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push(new Date(year, month, i));
  }
  
  // Add padding days from next month
  const endPadding = 6 - lastDay.getDay();
  for (let i = 1; i <= endPadding; i++) {
    days.push(new Date(year, month + 1, i));
  }
  
  return days;
}

export default function SessionManagement() {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const { loading, user } = useAuth();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <AlertTriangle className="h-12 w-12 text-amber-500" />
          <h1 className="text-2xl font-semibold">Zugriff verweigert</h1>
          <p className="text-muted-foreground text-center">
            Sie benötigen Kursadmin- oder Admin-Rechte für diese Seite.
          </p>
          <Link href="/">
            <Button>Zur Startseite</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}>
      <SessionManagementContent setSidebarWidth={setSidebarWidth} />
    </SidebarProvider>
  );
}

function SessionManagementContent({ setSidebarWidth }: { setSidebarWidth: (width: number) => void }) {
  const isMobile = useIsMobile();
  const utils = trpc.useUtils();
  
  // View state
  const [activeTab, setActiveTab] = useState<"calendar" | "list">("list");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Filter state
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>();
  const [selectedInstructorId, setSelectedInstructorId] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "cancelled">("all");
  const [dateRangeStart, setDateRangeStart] = useState<string>("");
  const [dateRangeEnd, setDateRangeEnd] = useState<string>("");
  
  // Selection state for bulk operations
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [rangeStart, setRangeStart] = useState<string | null>(null);
  
  // Dialog state
  const [bulkActionDialog, setBulkActionDialog] = useState<{
    open: boolean;
    action: "cancel" | "reactivate" | "delete";
    startDate: string;
    endDate: string;
  } | null>(null);

  const [sendNotifications, setSendNotifications] = useState(true);
  const [cancellationReason, setCancellationReason] = useState("");
  
  // Individual session action dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelSessionId, setCancelSessionId] = useState<number | null>(null);
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [reactivateSessionId, setReactivateSessionId] = useState<number | null>(null);
  const [deleteSessionId, setDeleteSessionId] = useState<number | null>(null);
  
  // Fetch data
  const { data: courses } = trpc.course.list.useQuery({ includeArchived: false });
  const { data: instructors } = trpc.instructor.list.useQuery();
  // Get today's date for filtering future sessions
  const today = useMemo(() => formatDateForInput(new Date()), []);
  
  const { data: sessions, isLoading: sessionsLoading } = trpc.session.getAllWithDetails.useQuery({
    courseId: selectedCourseId,
    instructorId: selectedInstructorId,
    status: statusFilter === "all" ? undefined : statusFilter,
    startDate: dateRangeStart || today, // Default to today to show only future sessions
    endDate: dateRangeEnd || undefined,
  });
  // Calculate start and end dates for the current month view
  const monthStart = useMemo(() => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return formatDateForInput(d);
  }, [currentMonth]);
  
  const monthEnd = useMemo(() => {
    const d = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    return formatDateForInput(d);
  }, [currentMonth]);
  
  const { data: sessionCounts } = trpc.session.getCountsByDate.useQuery({
    startDate: monthStart,
    endDate: monthEnd,
  });
  
  // Mutations
  const bulkCancelMutation = trpc.session.bulkCancel.useMutation({
    onSuccess: (result) => {
      let message = `${result.count} Termine abgesagt`;
      if (result.participantsNotified && result.participantsNotified > 0) {
        message += ` • ${result.emailsSent} E-Mails gesendet`;
        if (result.emailsFailed && result.emailsFailed > 0) {
          message += ` (${result.emailsFailed} fehlgeschlagen)`;
        }
      }
      toast.success(message);
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setBulkActionDialog(null);
      setSelectedDates(new Set());
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  const bulkReactivateMutation = trpc.session.bulkReactivate.useMutation({
    onSuccess: (result) => {
      let message = `${result.count} Termine reaktiviert`;
      if (result.participantsNotified && result.participantsNotified > 0) {
        message += ` • ${result.emailsSent} E-Mails gesendet`;
        if (result.emailsFailed && result.emailsFailed > 0) {
          message += ` (${result.emailsFailed} fehlgeschlagen)`;
        }
      }
      toast.success(message);
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setBulkActionDialog(null);
      setSelectedDates(new Set());
      // Reset notification state
      setSendNotifications(true);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  const bulkDeleteMutation = trpc.session.bulkDelete.useMutation({
    onSuccess: (result) => {
      if (result.skipped > 0) {
        toast.success(`${result.count} Termine gelöscht, ${result.skipped} übersprungen (haben Anwesenheitsdaten)`);
      } else {
        toast.success(`${result.count} Termine gelöscht`);
      }
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setBulkActionDialog(null);
      setSelectedDates(new Set());
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
  
  // Individual session mutations
  const cancelSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setShowCancelDialog(false);
      setCancelSessionId(null);
      if (data.emailsSent && data.emailsSent > 0) {
        toast.success(`Termin abgesagt. ${data.emailsSent} Benachrichtigung(en) gesendet.`);
      } else {
        toast.success("Termin abgesagt");
      }
    },
    onError: () => toast.error("Fehler beim Absagen des Termins"),
  });
  
  const reactivateSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setShowReactivateDialog(false);
      setReactivateSessionId(null);
      if (data.emailsSent && data.emailsSent > 0) {
        toast.success(`Termin reaktiviert. ${data.emailsSent} Benachrichtigung(en) gesendet.`);
      } else {
        toast.success("Termin reaktiviert");
      }
    },
    onError: () => toast.error("Fehler beim Reaktivieren des Termins"),
  });
  
  const deleteSessionMutation = trpc.session.delete.useMutation({
    onSuccess: () => {
      utils.session.getAllWithDetails.invalidate();
      utils.session.getCountsByDate.invalidate();
      setDeleteSessionId(null);
      toast.success("Termin gelöscht");
    },
    onError: (error) => toast.error(error.message),
  });
  
  // Create a map of session counts by date
  const sessionCountMap = useMemo(() => {
    const map = new Map<string, { total: number; active: number; cancelled: number }>();
    sessionCounts?.forEach(sc => {
      map.set(sc.date, { total: sc.totalCount, active: sc.activeCount, cancelled: sc.cancelledCount });
    });
    return map;
  }, [sessionCounts]);
  
  // Calendar days
  const calendarDays = useMemo(() => {
    return getDaysInMonth(currentMonth.getFullYear(), currentMonth.getMonth());
  }, [currentMonth]);
  
  // Handle date click in calendar
  const handleDateClick = (date: Date) => {
    const dateStr = formatDateForInput(date);
    
    if (isSelectingRange) {
      if (!rangeStart) {
        setRangeStart(dateStr);
        setSelectedDates(new Set([dateStr]));
      } else {
        // Complete the range
        const start = rangeStart < dateStr ? rangeStart : dateStr;
        const end = rangeStart < dateStr ? dateStr : rangeStart;
        
        const newSelected = new Set<string>();
        const current = new Date(start + "T12:00:00Z");
        const endDate = new Date(end + "T12:00:00Z");
        
        while (current <= endDate) {
          newSelected.add(formatDateForInput(current));
          current.setDate(current.getDate() + 1);
        }
        
        setSelectedDates(newSelected);
        setRangeStart(null);
        setIsSelectingRange(false);
      }
    } else {
      // Toggle single date
      const newSelected = new Set(selectedDates);
      if (newSelected.has(dateStr)) {
        newSelected.delete(dateStr);
      } else {
        newSelected.add(dateStr);
      }
      setSelectedDates(newSelected);
    }
  };
  
  // Get selected date range
  const getSelectedDateRange = (): { start: string; end: string } | null => {
    if (selectedDates.size === 0) return null;
    const dates = Array.from(selectedDates).sort();
    return { start: dates[0], end: dates[dates.length - 1] };
  };
  
  // Open bulk action dialog
  const openBulkActionDialog = (action: "cancel" | "reactivate" | "delete") => {
    const range = getSelectedDateRange();
    if (!range) return;
    
    setBulkActionDialog({
      open: true,
      action,
      startDate: range.start,
      endDate: range.end,
    });
  };
  
  // Execute bulk action
  const executeBulkAction = () => {
    if (!bulkActionDialog) return;
    
    const params = {
      startDate: bulkActionDialog.startDate,
      endDate: bulkActionDialog.endDate,
      courseId: selectedCourseId,
      instructorId: selectedInstructorId,
    };
    
    switch (bulkActionDialog.action) {
      case "cancel":
        bulkCancelMutation.mutate({
          ...params,
          sendNotifications,
          cancellationReason: cancellationReason || undefined,
        });
        // Reset notification state after action
        setSendNotifications(true);
        setCancellationReason("");
        break;
      case "reactivate":
        bulkReactivateMutation.mutate({
          ...params,
          sendNotifications,
        });
        break;
      case "delete":
        bulkDeleteMutation.mutate(params);
        break;
    }
  };
  
  // Clear filters
  const clearFilters = () => {
    setSelectedCourseId(undefined);
    setSelectedInstructorId(undefined);
    setStatusFilter("all");
    setDateRangeStart("");
    setDateRangeEnd("");
  };
  
  const hasFilters = selectedCourseId || selectedInstructorId || statusFilter !== "all" || dateRangeStart || dateRangeEnd;

  return (
    <>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Terminverwaltung</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold">Terminverwaltung</h1>
              <p className="text-muted-foreground">
                Verwalten Sie Termine kursübergreifend
              </p>
            </div>
          </div>
          
          {/* Filters - Only shown in list view */}
          {activeTab === "list" && (
            <Card>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  <div>
                    <Label className="text-xs text-muted-foreground">Kurs</Label>
                    <Select 
                      value={selectedCourseId?.toString() || "all"} 
                      onValueChange={(v) => setSelectedCourseId(v === "all" ? undefined : parseInt(v))}
                    >
                      <SelectTrigger className="h-9 w-full">
                        <SelectValue placeholder="Alle Kurse" className="truncate" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Kurse</SelectItem>
                        {courses?.map(course => (
                          <SelectItem key={course.id} value={course.id.toString()}>
                            <span className="truncate">{course.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Kursleiter</Label>
                    <Select 
                      value={selectedInstructorId?.toString() || "all"} 
                      onValueChange={(v) => setSelectedInstructorId(v === "all" ? undefined : parseInt(v))}
                    >
                      <SelectTrigger className="h-9 w-full">
                        <SelectValue placeholder="Alle Kursleiter" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Kursleiter</SelectItem>
                        {instructors?.map(instructor => (
                          <SelectItem key={instructor.id} value={instructor.id.toString()}>
                            {instructor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select 
                      value={statusFilter} 
                      onValueChange={(v) => setStatusFilter(v as "all" | "active" | "cancelled")}
                    >
                      <SelectTrigger className="h-9 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle</SelectItem>
                        <SelectItem value="active">Aktiv</SelectItem>
                        <SelectItem value="cancelled">Abgesagt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {hasFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9">
                      <X className="h-4 w-4 mr-1" />
                      Filter zurücksetzen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Tabs and Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "calendar" | "list")}>
              <TabsList>
                <TabsTrigger value="list">
                  <List className="h-4 w-4 mr-2" />
                  Liste
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  <Calendar className="h-4 w-4 mr-2" />
                  Kalender
                </TabsTrigger>
              </TabsList>
            </Tabs>
            
            {selectedDates.size > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground">
                  {selectedDates.size} {selectedDates.size === 1 ? "Tag" : "Tage"} ausgewählt
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openBulkActionDialog("cancel")}
                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                >
                  <Ban className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Absagen</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openBulkActionDialog("reactivate")}
                  className="text-green-600 border-green-300 hover:bg-green-50"
                >
                  <ArchiveRestore className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Reaktivieren</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openBulkActionDialog("delete")}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Löschen</span>
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedDates(new Set())}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
          
          {/* Calendar View */}
          {activeTab === "calendar" && (
            <Card>
              <CardContent className="p-4">
                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <h2 className="text-lg font-semibold">{getMonthName(currentMonth)}</h2>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
                
                {/* Range Selection Toggle */}
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Button
                    variant={isSelectingRange ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setIsSelectingRange(!isSelectingRange);
                      setRangeStart(null);
                    }}
                  >
                    {isSelectingRange ? "Bereichsauswahl aktiv" : "Bereich auswählen"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const today = new Date();
                      setCurrentMonth(today);
                    }}
                  >
                    Heute
                  </Button>
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Day Headers */}
                  {DAY_NAMES.map(day => (
                    <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                      {day}
                    </div>
                  ))}
                  
                  {/* Calendar Days */}
                  {calendarDays.map((date, index) => {
                    const dateStr = formatDateForInput(date);
                    const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                    const isToday = formatDateForInput(new Date()) === dateStr;
                    const isSelected = selectedDates.has(dateStr);
                    const counts = sessionCountMap.get(dateStr);
                    
                    return (
                      <button
                        key={index}
                        onClick={() => handleDateClick(date)}
                        className={`
                          relative p-2 min-h-[60px] text-left rounded-lg border transition-colors
                          ${!isCurrentMonth ? "opacity-40" : ""}
                          ${isToday ? "border-primary" : "border-transparent"}
                          ${isSelected ? "bg-primary/10 border-primary" : "hover:bg-muted/50"}
                        `}
                      >
                        <span className={`text-sm ${isToday ? "font-bold text-primary" : ""}`}>
                          {date.getDate()}
                        </span>
                        {counts && counts.total > 0 && (
                          <div className="absolute bottom-1 right-1 flex items-center gap-0.5">
                            {counts.active > 0 && (
                              <span className="w-2 h-2 rounded-full bg-green-500" title={`${counts.active} aktiv`} />
                            )}
                            {counts.cancelled > 0 && (
                              <span className="w-2 h-2 rounded-full bg-red-500" title={`${counts.cancelled} abgesagt`} />
                            )}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* List View */}
          {activeTab === "list" && (
            <Card>
              <CardContent className="p-4">
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h3 className="font-semibold">Kommende Termine</h3>
                    <p className="text-xs text-muted-foreground">Vergangene Termine werden ausgeblendet</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-600" />
                      Anwesenheit erfasst
                    </span>
                  </div>
                </div>
                
                {sessionsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Lade Termine...
                  </div>
                ) : sessions && sessions.length > 0 ? (
                  <div className="space-y-2">
                    {sessions.map(sessionData => (
                      <div 
                        key={sessionData.session.id}
                        className={`
                          flex items-center gap-3 p-3 rounded-lg border
                          ${sessionData.session.isCancelled ? "bg-red-50 border-red-200" : "bg-background hover:bg-muted/50"}
                        `}
                      >
                        {/* Left: Date and course info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{formatDate(sessionData.session.sessionDate)}</span>
                            {!!sessionData.session.isCancelled && (
                              <Badge variant="destructive" className="text-xs">Abgesagt</Badge>
                            )}
                            {!sessionData.session.isCancelled && sessionData.hasAttendance && (
                              <Badge variant="outline" className="gap-1 text-green-600 border-green-600 text-xs">
                                <CheckCircle2 className="h-3 w-3" />
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {sessionData.course.name} • {sessionData.instructor?.name || "Kein Kursleiter"}
                          </div>
                        </div>
                        {/* Right: Action buttons - always visible, fixed width */}
                        <div className="flex items-center gap-1 shrink-0">
                          {/* Cancel button - only for active sessions */}
                          {!sessionData.session.isCancelled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-amber-600 hover:text-amber-600 hover:bg-amber-600/10"
                              onClick={() => {
                                setCancelSessionId(sessionData.session.id);
                                setShowCancelDialog(true);
                              }}
                            >
                              <XCircle className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Absagen</span>
                            </Button>
                          )}
                          {/* Reactivate button - only for cancelled sessions */}
                          {!!sessionData.session.isCancelled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-green-600 hover:text-green-600 hover:bg-green-600/10"
                              onClick={() => {
                                setReactivateSessionId(sessionData.session.id);
                                setShowReactivateDialog(true);
                              }}
                            >
                              <ArchiveRestore className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Reaktivieren</span>
                            </Button>
                          )}
                          {/* Delete button - only if no attendance recorded */}
                          {!sessionData.hasAttendance && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setDeleteSessionId(sessionData.session.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Löschen</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    Keine Termine gefunden
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </main>
      </SidebarInset>
      
      {/* Bulk Action Confirmation Dialog */}
      <AlertDialog 
        open={bulkActionDialog?.open || false} 
        onOpenChange={(open) => !open && setBulkActionDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkActionDialog?.action === "cancel" && "Termine absagen"}
              {bulkActionDialog?.action === "reactivate" && "Termine reaktivieren"}
              {bulkActionDialog?.action === "delete" && "Termine löschen"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkActionDialog && (
                <>
                  Möchten Sie wirklich alle{" "}
                  {statusFilter === "active" ? "aktiven " : statusFilter === "cancelled" ? "abgesagten " : ""}
                  Termine vom{" "}
                  <strong>{formatDate(bulkActionDialog.startDate)}</strong> bis{" "}
                  <strong>{formatDate(bulkActionDialog.endDate)}</strong>
                  {selectedCourseId && courses && (
                    <> für den Kurs <strong>{courses.find(c => c.id === selectedCourseId)?.name}</strong></>
                  )}
                  {selectedInstructorId && instructors && (
                    <> von <strong>{instructors.find(i => i.id === selectedInstructorId)?.name}</strong></>
                  )}
                  {" "}
                  {bulkActionDialog.action === "cancel" && "absagen"}
                  {bulkActionDialog.action === "reactivate" && "reaktivieren"}
                  {bulkActionDialog.action === "delete" && "löschen"}
                  ?
                  {bulkActionDialog.action === "delete" && (
                    <p className="mt-2 text-amber-600">
                      <AlertTriangle className="inline h-4 w-4 mr-1" />
                      Termine mit Anwesenheitsdaten werden übersprungen.
                    </p>
                  )}
                </>
              )}
            </AlertDialogDescription>
            
            {/* Notification options for cancel action */}
            {bulkActionDialog?.action === "cancel" && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sendNotifications"
                    checked={sendNotifications}
                    onChange={(e) => setSendNotifications(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="sendNotifications" className="text-sm font-normal cursor-pointer">
                    Teilnehmer per E-Mail benachrichtigen
                  </Label>
                </div>
                {sendNotifications && (
                  <div>
                    <Label htmlFor="cancellationReason" className="text-sm">Grund (optional)</Label>
                    <Input
                      id="cancellationReason"
                      placeholder="z.B. Betriebsferien, Krankheit..."
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Notification options for reactivate action */}
            {bulkActionDialog?.action === "reactivate" && (
              <div className="py-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="sendNotificationsReactivate"
                    checked={sendNotifications}
                    onChange={(e) => setSendNotifications(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="sendNotificationsReactivate" className="text-sm font-normal cursor-pointer">
                    Teilnehmer per E-Mail benachrichtigen
                  </Label>
                </div>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeBulkAction}
              className={
                bulkActionDialog?.action === "delete" 
                  ? "bg-red-600 hover:bg-red-700" 
                  : bulkActionDialog?.action === "cancel"
                  ? "bg-amber-600 hover:bg-amber-700"
                  : ""
              }
            >
              {bulkActionDialog?.action === "cancel" && "Absagen"}
              {bulkActionDialog?.action === "reactivate" && "Reaktivieren"}
              {bulkActionDialog?.action === "delete" && "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Individual Session Cancel Dialog */}
      <CancelSessionDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={({ reason, sendEmail }) => {
          if (cancelSessionId) {
            cancelSessionMutation.mutate({
              id: cancelSessionId,
              isCancelled: true,
              sendCancellationEmail: sendEmail,
              cancellationReason: reason,
            });
          }
        }}
        isPending={cancelSessionMutation.isPending}
      />
      
      {/* Individual Session Reactivate Dialog */}
      <ReactivateSessionDialog
        open={showReactivateDialog}
        onOpenChange={setShowReactivateDialog}
        onConfirm={({ sendEmail }) => {
          if (reactivateSessionId) {
            reactivateSessionMutation.mutate({
              id: reactivateSessionId,
              isCancelled: false,
              sendReactivationEmail: sendEmail,
            });
          }
        }}
        isPending={reactivateSessionMutation.isPending}
      />
      
      {/* Individual Session Delete Confirmation */}
      <AlertDialog open={deleteSessionId !== null} onOpenChange={(open) => !open && setDeleteSessionId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Termin löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie diesen Termin wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteSessionId) {
                  deleteSessionMutation.mutate({ id: deleteSessionId });
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteSessionMutation.isPending}
            >
              {deleteSessionMutation.isPending ? "Lösche..." : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
